/**
 * The grid of a border on an elevation sheet (ADR-0020, tiers B and C), on the sheet
 * and in the side panel. A border is cut into rows by its floor lines and into bays by
 * its bay lines; each cell holding a pane is a unit, and a typical row counts ×N.
 */
import { useEffect, useState } from "react";

import type { WorkspaceConfig, WorkspaceOp } from "@/api/client";
import { feetInches } from "@/flow/workspace";
import { rowsOf, typicalOf } from "@/flow/grid";
import type { GridLine } from "@/flow/grid";
import { cn } from "@/ui/cn";
import type { SheetGeometry } from "@/viewer/SheetCanvas";

/** Keys of configuration mode on an elevation sheet. */
export const GRID_KEYS: readonly [string, string][] = [
  ["J / K", "Next or previous border"],
  ["A", "Approve this border"],
  ["Enter", "Approve the sheet and go to the next"],
  ["N", "Draw a border around an elevation"],
  ["Shift+N", "Draw an inner border, with its own grid"],
  ["X", "Draw a hole, left out of the take-off"],
  ["H", "A floor line at the pointer"],
  ["V", "A bay line at the pointer (Shift: this row only)"],
  ["Click", "Choose a line; arrows move it, Del takes it away"],
  ["R", "Repeat the chosen line at its spacing"],
  ["T", "Typical: the row at the pointer counts ×N"],
  ["B", "Redraw this border"],
  ["P / U", "Its panes, or its units"],
  ["Z", "Undo"],
];

/** The chosen border's bay lines, the chosen line, and its typical rows' counts. */
export function GridLines({
  geometry,
  config,
  line,
}: {
  geometry: SheetGeometry;
  config: WorkspaceConfig;
  line: GridLine | null;
}) {
  const b = config.bbox_norm;
  const font = geometry.viewWidth / 110;
  const across = (y: number) => geometry.px({ x0: b.x0, y0: y, x1: b.x1, y1: y });
  return (
    <g data-grid>
      {config.vlines.map((v) => {
        const r = geometry.px({ x0: v.x, y0: v.y0 ?? b.y0, x1: v.x, y1: v.y1 ?? b.y1 });
        const on = line?.kind === "vline" && Math.abs(line.at - v.x) < 1e-6;
        return (
          <line
            key={`${v.x}:${v.y0}`}
            x1={r.x}
            x2={r.x}
            y1={r.y}
            y2={r.y + r.height}
            stroke="var(--color-violet)"
            strokeWidth={on ? 4 : 2}
            vectorEffect="non-scaling-stroke"
            data-vline
            data-on={on || undefined}
          />
        );
      })}
      {line?.kind === "break" ? (
        <line
          x1={across(line.at).x}
          x2={across(line.at).x + across(line.at).width}
          y1={across(line.at).y}
          y2={across(line.at).y}
          stroke="var(--color-violet)"
          strokeWidth={4}
          vectorEffect="non-scaling-stroke"
          data-break
          data-on
        />
      ) : null}
      {rowsOf(config).map(([y0, y1], row) => {
        const n = typicalOf(config, row);
        if (n <= 1) return null;
        const r = geometry.px({ x0: b.x1, y0, x1: b.x1, y1 });
        return (
          <text
            key={row}
            x={r.x + font * 0.5}
            y={r.y + r.height / 2}
            fontSize={font}
            fontWeight={600}
            fill="var(--color-violet)"
            data-typical={row}
          >
            ×{n} typical
          </text>
        );
      })}
    </g>
  );
}

/** The border's rows: their heights, and how many floors each stands for. */
export function GridPanel({
  config,
  heightPt,
  row,
  canEdit,
  onOps,
  onDone,
}: {
  config: WorkspaceConfig;
  heightPt: number;
  /** The row whose typical count is being set (T), or null. */
  row: number | null;
  canEdit: boolean;
  onOps: (ops: WorkspaceOp[]) => void;
  onDone: () => void;
}) {
  const rows = rowsOf(config);
  const ppi = config.scale?.pt_per_inch ?? null;
  return (
    <section aria-label="Rows" className="space-y-1">
      <h3 className="text-xs font-semibold tracking-wide text-muted uppercase">
        Rows ({rows.length}) · {config.vlines.length} bay{" "}
        {config.vlines.length === 1 ? "line" : "lines"}
      </h3>
      <ul className="divide-y divide-line rounded-md border border-line">
        {rows.map(([y0, y1], r) => (
          <li
            key={r}
            className={cn(
              "flex items-center justify-between gap-2 px-2 py-1 text-sm",
              r === row ? "bg-accent-soft" : "",
            )}
          >
            <span className="text-ink-2">
              Row {r + 1}
              {ppi ? (
                <span className="ml-1 text-xs text-muted">
                  {feetInches(Math.round(((y1 - y0) * heightPt) / ppi))}
                </span>
              ) : null}
            </span>
            {r === row && canEdit ? (
              <TypicalInput
                initial={typicalOf(config, r)}
                onSet={(count) => {
                  onOps([{ op: "set_typical", configuration_id: config.id, row: r, count }]);
                  onDone();
                }}
                onDone={onDone}
              />
            ) : typicalOf(config, r) > 1 ? (
              <span className="text-xs font-semibold text-violet">×{typicalOf(config, r)}</span>
            ) : null}
          </li>
        ))}
      </ul>
    </section>
  );
}

function TypicalInput({
  initial,
  onSet,
  onDone,
}: {
  initial: number;
  onSet: (count: number) => void;
  onDone: () => void;
}) {
  const [value, setValue] = useState(String(initial));
  useEffect(() => setValue(String(initial)), [initial]);
  return (
    <form
      onSubmit={(event) => {
        event.preventDefault();
        const n = Math.floor(Number(value));
        if (Number.isFinite(n) && n >= 1) onSet(n);
        else onDone();
      }}
    >
      <input
        autoFocus
        aria-label="Typical count"
        inputMode="numeric"
        className="w-14 rounded-md border border-line-strong bg-surface px-1.5 py-0.5 text-right text-sm"
        value={value}
        onChange={(e) => setValue(e.target.value)}
        onBlur={onDone}
        onKeyDown={(e) => e.key === "Escape" && onDone()}
      />
    </form>
  );
}
